// src/pages/children/ChildDetail.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip
} from '@mui/material';
import { 
  Person as PersonIcon, 
  School as SchoolIcon, 
  ArrowBack as ArrowBackIcon, 
  Save as SaveIcon, 
  Delete as DeleteIcon
} from '@mui/icons-material';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';

const ChildDetail = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [child, setChild] = useState(null);
  const [parent, setParent] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    curso: '',
    clase: ''
  });
  const [enrollments, setEnrollments] = useState([]);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchChildData = async () => {
      try {
        // Obtener datos del hijo
        const childDoc = await getDoc(doc(db, 'children', childId));
        if (childDoc.exists()) {
          const childData = childDoc.data();
          setChild({ id: childDoc.id, ...childData });
          setFormData({
            nombre: childData.nombre || '',
            apellidos: childData.apellidos || '',
            curso: childData.curso || '',
            clase: childData.clase || ''
          });
          
          // Obtener datos del padre
          if (childData.parentId) {
            try {
              const parentDoc = await getDoc(doc(db, 'users', childData.parentId));
              if (parentDoc.exists()) {
                setParent({ id: parentDoc.id, ...parentDoc.data() });
              }
            } catch (error) {
              console.error('Error fetching parent:', error);
            }
          }
          
          // Obtener inscripciones en servicios
          try {
            const enrollmentsQuery = query(
              collection(db, 'serviceEnrollments'),
              where('childrenIds', 'array-contains', childId)
            );
            const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
            const enrollmentsList = [];
            
            for (const docSnapshot of enrollmentsSnapshot.docs) {
              const enrollment = docSnapshot.data();
              const serviceId = enrollment.serviceId;
              
              try {
                const serviceDoc = await getDoc(doc(db, 'services', serviceId));
                if (serviceDoc.exists()) {
                  const serviceData = serviceDoc.data();
                  enrollmentsList.push({
                    id: docSnapshot.id,
                    ...enrollment,
                    serviceName: serviceData.nombre || 'Servicio sin nombre',
                    serviceDetails: serviceData
                  });
                } else {
                  enrollmentsList.push({
                    id: docSnapshot.id,
                    ...enrollment,
                    serviceName: 'Servicio no encontrado'
                  });
                }
              } catch (error) {
                console.error(`Error fetching service ${serviceId}:`, error);
              }
            }
            
            setEnrollments(enrollmentsList);
          } catch (error) {
            console.error('Error fetching enrollments:', error);
          }
        } else {
          setError('No se encontró el hijo');
          navigate('/admin/children');
        }
      } catch (error) {
        console.error('Error fetching child data:', error);
        setError('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    fetchChildData();
  }, [childId, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    
    try {
      await updateDoc(doc(db, 'children', childId), formData);
      setSuccess(true);
      
      // Actualizar datos locales
      setChild({
        ...child,
        ...formData
      });
    } catch (error) {
      console.error('Error updating child:', error);
      setError('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDelete = async () => {
    try {
      // Clean up serviceEnrollments referencing this child
      const enrollmentsQuery = query(
        collection(db, 'serviceEnrollments'),
        where('childrenIds', 'array-contains', childId)
      );
      const enrollmentsSnap = await getDocs(enrollmentsQuery);
      for (const enrollmentDoc of enrollmentsSnap.docs) {
        const currentIds = enrollmentDoc.data().childrenIds || [];
        await updateDoc(enrollmentDoc.ref, {
          childrenIds: currentIds.filter(id => id !== childId)
        });
      }

      await deleteDoc(doc(db, 'children', childId));
      navigate('/admin/children');
    } catch (error) {
      console.error('Error deleting child:', error);
      setError('Error al eliminar el hijo');
      setDeleteDialogOpen(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Fecha no disponible';
    
    const date = timestamp?.toDate ? 
      timestamp.toDate() : 
      new Date(timestamp?.seconds * 1000 || Date.now());
      
    return date.toLocaleDateString('es-ES');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Detalle del Hijo</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/children')}
        >
          Volver
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Información del hijo */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
              <Typography variant="h6">
                Información del Hijo
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Apellidos"
                  name="apellidos"
                  value={formData.apellidos}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Curso"
                  name="curso"
                  value={formData.curso}
                  onChange={handleInputChange}
                  placeholder="Ej: 3º Primaria"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Clase"
                  name="clase"
                  value={formData.clase}
                  onChange={handleInputChange}
                  placeholder="Ej: A"
                />
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteDialogOpen}
              >
                Eliminar
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                Guardar cambios
              </Button>
            </Box>
          </Paper>
          
          {/* Información del padre */}
          {parent && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ fontSize: 40, mr: 2, color: 'secondary.main' }} />
                <Typography variant="h6">
                  Información del Padre/Madre
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <List disablePadding>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${parent.firstName || ''} ${parent.lastName || ''}`}
                    secondary="Nombre completo"
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemText
                    primary={parent.email}
                    secondary="Email"
                    sx={{ pl: 7 }}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemText
                    primary={parent.phone || 'No especificado'}
                    secondary="Teléfono"
                    sx={{ pl: 7 }}
                  />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/admin/users/${parent.id}`)}
                >
                  Ver perfil completo
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>
        
        {/* Inscripciones en servicios */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SchoolIcon sx={{ fontSize: 40, mr: 2, color: 'warning.main' }} />
              <Typography variant="h6">
                Servicios Inscritos
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            {enrollments.length === 0 ? (
              <Typography align="center" color="textSecondary" sx={{ py: 4 }}>
                Este hijo no está inscrito en ningún servicio
              </Typography>
            ) : (
              <List>
                {enrollments.map((enrollment) => (
                  <Card key={enrollment.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {enrollment.serviceName}
                      </Typography>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            Fecha de inscripción:
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(enrollment.enrollmentDate)}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            Estado:
                          </Typography>
                          <Chip 
                            label={enrollment.status || 'Activo'} 
                            color={enrollment.status === 'cancelled' ? 'error' : 'success'}
                            size="small"
                          />
                        </Grid>
                        
                        {enrollment.serviceDetails && (
                          <>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textSecondary">
                                Fechas del servicio:
                              </Typography>
                              <Typography variant="body2">
                                {formatDate(enrollment.serviceDetails.fechaInicio)} - {formatDate(enrollment.serviceDetails.fechaFin)}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textSecondary">
                                Lugar:
                              </Typography>
                              <Typography variant="body2">
                                {enrollment.serviceDetails.lugar || 'No especificado'}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>
                      
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          onClick={() => navigate(`/admin/services/${enrollment.serviceId}`)}
                        >
                          Ver servicio
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>¿Eliminar hijo?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres eliminar a {child?.nombre} {child?.apellidos}? Esta acción no se puede deshacer.
            {enrollments.length > 0 && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Atención: Este hijo está inscrito en {enrollments.length} servicio(s). Si lo eliminas, puede afectar a estas inscripciones.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={success}
        autoHideDuration={4000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success">
          Datos guardados correctamente
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChildDetail;